import moment from 'moment-timezone';

// Calendar-day coordinates avoid daylight-saving changes in column widths.
export const parseGanttDay = (value, timezone) => {
    if (value === null || value === undefined || value === '') return null;
    let input = value;
    if (typeof value === 'object' && typeof value.toDate === 'function') input = value.toDate();
    else if (typeof value === 'object' && (value.seconds !== undefined || value._seconds !== undefined)) {
        input = Number(value.seconds ?? value._seconds) * 1000;
    }
    let date = null;
    if (typeof input === 'string') {
        const text = input.trim();
        const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
        date = timezone && hasZone
            ? moment(text).tz(timezone)
            : timezone
                ? moment.tz(text, [moment.ISO_8601, 'DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'], true, timezone)
                : moment(text, [moment.ISO_8601, 'DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'], true);
    } else if (input instanceof Date || moment.isMoment(input) || typeof input === 'number') {
        date = timezone ? moment(input).tz(timezone) : moment(input);
    }
    return date?.isValid() ? Math.floor(Date.UTC(date.year(), date.month(), date.date()) / 86400000) : null;
};

export const parseGanttEndDay = (value, timezone = moment.tz.guess()) => {
    if (value === null || value === undefined || value === '') return null;
    let input = value;
    if (typeof value === 'object' && typeof value.toDate === 'function') input = value.toDate();
    else if (typeof value === 'object' && (value.seconds !== undefined || value._seconds !== undefined)) {
        input = Number(value.seconds ?? value._seconds) * 1000;
    }
    const includesTime = typeof input !== 'string' || /(?:T|\s)\d{1,2}:\d{2}/.test(input);
    let date;
    if (typeof input === 'string') {
        const text = input.trim();
        const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
        date = hasZone
            ? moment(text).tz(timezone)
            : moment.tz(text, [moment.ISO_8601, 'DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'], true, timezone);
    } else {
        date = moment(input).tz(timezone);
    }
    if (!date.isValid()) return null;
    // Older Gantt edits ended eligibility at 02:00 on the following day.
    // Display that boundary as occupying the preceding calendar day.
    if (includesTime && date.hour() === 2 && date.minute() === 0 && date.second() === 0) date.subtract(1, 'day');
    return Math.floor(Date.UTC(date.year(), date.month(), date.date()) / 86400000);
};

export const formatGanttDay = (day, format = 'D MMM YYYY') => moment.utc(day * 86400000).format(format);

// Tasks have no editable eligibility dates: their expiredDate is a plan expiry
// that the editor resets on every save, so task bars summarize descendants.
export const getGanttDateFields = kind => ({
    subtask: { from: 'validFromDate', until: 'expiryDate' },
    subsubtask: { from: 'validFrom', until: 'expiryDate' },
    todo: { from: 'deferUntil', until: 'deferUntil' },
}[kind]);

const firstDate = (item, fields, timezone, isEnd = false) => {
    for (const field of fields) {
        const day = isEnd ? parseGanttEndDay(item[field], timezone) : parseGanttDay(item[field], timezone);
        if (day !== null) return day;
    }
    return null;
};

export const getGanttInterval = (item, today, kind, timezone) => {
    const from = firstDate(item, ['validFrom', 'validFromDate', 'startDate', 'start', ...(kind === 'todo' ? ['deferUntil'] : [])], timezone);
    // expiryDate is the stored end field; validUntil is a legacy alias that Edit.js migrates away.
    const until = firstDate(item, ['expiryDate', 'validUntil', 'validUntilDate', 'expiredDate', 'endDate', 'dueDate', 'end'], timezone, true);
    if (from === null && until === null) return null;
    if (kind === 'todo') {
        const day = from ?? until;
        return { start: day, end: day, open: false, from, until };
    }
    if (from !== null && until !== null && from > until) return { invalid: true, from, until };
    // An expired, deadline-only item has no remaining eligibility. Keep its deadline visible.
    if (from === null && until < today) return { start: until, end: until, expired: true, from, until };
    return { start: from ?? today, end: until ?? Infinity, open: until === null, from, until };
};

export const overlapsGanttRange = (interval, range) => Boolean(interval && !interval.invalid && !interval.expired
    && interval.start <= range.end && interval.end >= range.start);

export const isGanttRowVisible = (row, start, end) => row.intervals.some(interval => (
    !interval.invalid && interval.start <= end && interval.end >= start
));

export const summarizeHiddenGanttRows = (rows, start, end) => {
    const hiddenRows = rows.filter(row => !isGanttRowVisible(row, start, end));
    let latestBefore = null;
    let earliestAfter = null;

    hiddenRows.forEach(row => row.intervals.filter(interval => !interval.invalid).forEach(interval => {
        if (Number.isFinite(interval.end) && interval.end < start) {
            latestBefore = latestBefore === null ? interval.end : Math.max(latestBefore, interval.end);
        }
        if (Number.isFinite(interval.start) && interval.start > end) {
            earliestAfter = earliestAfter === null ? interval.start : Math.min(earliestAfter, interval.start);
        }
    }));

    return { count: hiddenRows.length, latestBefore, earliestAfter };
};

export const buildGanttRows = (tasks, todos, today, todoVersion = 0, timezone = moment.tz.guess()) => {
    const rows = [];
    const makeRow = (item, kind, key, depth, identity, version) => {
        const dateFields = getGanttDateFields(kind);
        const interval = dateFields ? getGanttInterval(item, today, kind, timezone) : null;
        return { key, kind, depth, name: item.tname || item.sname || item.name || item.content || item.description || item.id || 'Untitled',
            intervals: interval ? [interval] : [], derived: false, identity, version: Number.isInteger(version) ? version : 0,
            dateFields, sourceDates: dateFields ? { [dateFields.from]: item[dateFields.from], [dateFields.until]: item[dateFields.until] } : {} };
    };
    tasks.forEach((task, taskIndex) => {
        const taskIdentity = { taskPracticalId: task.practicalId };
        const taskRow = makeRow(task, 'task', `task-${taskIndex}`, 0, taskIdentity, task.version);
        rows.push(taskRow);
        const descendants = [];
        (Array.isArray(task.subs) ? task.subs : []).forEach((sub, subIndex) => {
            const subIdentity = { taskPracticalId: task.practicalId, sn: sub.sn };
            const subRow = makeRow(sub, 'subtask', `${taskRow.key}-sub-${subIndex}`, 1, subIdentity, task.version);
            rows.push(subRow);
            descendants.push(...subRow.intervals);
            (Array.isArray(sub.subsubs) ? sub.subsubs : []).forEach((subsub, index) => {
                const identity = { taskPracticalId: task.practicalId, subtaskSn: sub.sn, sn: subsub.sn };
                const row = makeRow(subsub, 'subsubtask', `${subRow.key}-subsub-${index}`, 2, identity, task.version);
                rows.push(row);
                descendants.push(...row.intervals);
            });
        });
        taskRow.intervals = descendants.filter(interval => !interval.invalid);
        taskRow.derived = taskRow.intervals.length > 0;
    });
    todos.forEach((todo, index) => rows.push(makeRow(todo, 'todo', `todo-${index}`, 0, { todoId: todo.todoId }, todoVersion)));
    return rows;
};

export const createGanttDatePatch = (row, action, nextStart, nextEnd, timezone = moment.tz.guess()) => {
    const fields = row.dateFields || getGanttDateFields(row.kind);
    if (!fields) return null;
    // Starts are 02:00 (the app's day boundary). Ends are 23:59:59 on the end
    // day, matching what the editors write, so Edit shows the same end day.
    const boundary = (day, isEnd = false) => {
        const date = moment.tz(formatGanttDay(day, 'YYYY-MM-DD'), 'YYYY-MM-DD', timezone).startOf('day');
        return (isEnd ? date.add(1, 'day').subtract(1, 'second') : date.add(2, 'hours')).toISOString();
    };
    if (row.kind === 'todo') return action === 'move' ? { [fields.from]: boundary(nextStart) } : null;

    const interval = row.intervals[0];
    if (!interval || row.derived) return null;
    // Only write dates the item already has (or the handle the user dragged).
    // A missing start is drawn from today and a missing end at the view edge;
    // neither placeholder should be saved.
    if (action === 'move') {
        const patch = {
            ...(interval.from !== null ? { [fields.from]: boundary(nextStart) } : {}),
            ...(interval.until !== null ? { [fields.until]: boundary(nextEnd, true) } : {}),
        };
        return Object.keys(patch).length ? patch : null;
    }
    if (action === 'resize-start') return { [fields.from]: boundary(nextStart) };
    if (action === 'resize-end') return { [fields.until]: boundary(nextEnd, true) };
    return null;
};

const patchItem = (item, patch) => {
    const next = { ...item };
    Object.entries(patch).forEach(([field, value]) => {
        if (value === undefined) delete next[field];
        else next[field] = value;
    });
    return next;
};

export const patchGanttData = (data, row, patch) => {
    if (row.kind === 'todo') {
        return { ...data, todos: data.todos.map(todo => todo.todoId === row.identity.todoId ? patchItem(todo, patch) : todo) };
    }
    return { ...data, tasks: data.tasks.map(task => {
        if (task.practicalId !== row.identity.taskPracticalId) return task;
        if (row.kind === 'task') return patchItem(task, patch);
        return { ...task, subs: (task.subs || []).map(subtask => {
            if (subtask.sn !== (row.kind === 'subtask' ? row.identity.sn : row.identity.subtaskSn)) return subtask;
            if (row.kind === 'subtask') return patchItem(subtask, patch);
            return { ...subtask, subsubs: (subtask.subsubs || []).map(subsubtask => (
                subsubtask.sn === row.identity.sn ? patchItem(subsubtask, patch) : subsubtask
            )) };
        }) };
    }) };
};

export const setGanttDataVersion = (data, row, version) => {
    if (row.kind === 'todo') return { ...data, todoVersion: version };
    return { ...data, tasks: data.tasks.map(task => task.practicalId === row.identity.taskPracticalId
        ? { ...task, version } : task) };
};

export const ganttIntervalLabel = (interval) => {
    if (interval.invalid) return 'Invalid date range: start is after end';
    if (interval.expired) return `Expired ${formatGanttDay(interval.until)}`;
    if (interval.open) return `From ${formatGanttDay(interval.start)} · no end date`;
    if (interval.start === interval.end) return formatGanttDay(interval.start);
    return `${formatGanttDay(interval.start)} – ${formatGanttDay(interval.end)}`;
};

export const getGanttColumns = (start, mode) => {
    const step = mode === 'day' ? 1 : 7;
    return Array.from({ length: mode === 'day' ? 42 : 16 }, (_, index) => ({ start: start + index * step, end: start + (index + 1) * step - 1 }));
};

export const startOfGanttWeek = day => day - ((moment.utc(day * 86400000).isoWeekday() + 6) % 7);
